import { Form } from "antd";
import { useEffect, useState } from "react";
import {
  addIncidentType,
  deleteIncidentType,
  updateIncidentType,
} from "../repository/data.repository";
import DynamicForm from "../controls/DynamicForm";
import { IncidentType } from "../repository/data.default";
import { IncidentTypesForm } from "./IncidentTypesFormDialog.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import ModalFooter from "../controls/ModalFooter";
import ModalDeleteConfirmation from "../controls/ModalDeleteConfirmation";
import ModalErrorAlert from "../controls/ModalErrorAlert";
import { hasFormTouched } from "../utils/hasFormChanged";
import { setFormFields } from "../utils/setFormFields";
import { getProblemMessage } from "../utils/http";

export interface IncidentTypesFormDialogProps {
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete";
  readonly initialValues?: IncidentType;
  readonly onFinish?: () => void;
  readonly onCancel?: () => void;
}

function IncidentTypesFormDialog(props: IncidentTypesFormDialogProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<IncidentTypesForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.open) {
      if (props.initialValues) {
        setFormFields(form, props.initialValues);
      }
    } else {
      reset();
    }
  }, [props.open]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  function onSubmit(): void {
    const { name, description } = values;
    setLoading(true);
    setErrorMessage(undefined);
    if (props.mode === "add") {
      addIncidentType({
        name,
        description,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    } else if (props.mode === "update") {
      if (hasFormTouched(form)) {
        updateIncidentType({
          uid: props.initialValues?.uid,
          rowVersion: props.initialValues?.rowVersion,
          name,
          description,
        })
          .then(() => {
            setLoading(false);
            props.onFinish?.();
          })
          .catch((err) => {
            setLoading(false);
            setErrorMessage(getProblemMessage(err));
          });
      } else {
        setLoading(false);
        props.onFinish?.();
      }
    } else if (props.mode === "delete") {
      deleteIncidentType(props.initialValues?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch((err) => {
          setLoading(false);
          setErrorMessage(getProblemMessage(err));
        });
    }
  }

  function onCancel(): void {
    props.onCancel?.();
  }

  // Methods

  function reset(): void {
    setValid(false);
    setErrorMessage(undefined);
    setLoading(false);
    form.resetFields();
  }

  return (
    <InModal
      open={props.open}
      size={props.mode === "delete" ? "small" : "medium"}
      title={modalTitle(props.mode, "Incident Type", props.initialValues?.name)}
      onCancel={onCancel}
      actions={
        <ModalFooter
          mode={props.mode}
          loading={loading}
          valid={valid}
          onCancel={onCancel}
          onSubmit={onSubmit}
        />
      }
    >
      {props.mode !== "delete" && (
        <DynamicForm
          className="w-auto"
          form={form}
          schema={{
            groups: [
              {
                controls: [
                  {
                    type: "textbox",
                    name: "name",
                    label: "Name",
                    rules: [
                      {
                        required: true,
                        message: "Name is required",
                      },
                    ],
                  },
                  {
                    type: "textbox",
                    name: "description",
                    label: "Description",
                    rules: [
                      {
                        required: true,
                        message: "Description is required",
                      },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      )}
      {props.mode === "delete" && (
        <ModalDeleteConfirmation
          entityLabel="incident type"
          entityName={props.initialValues?.name}
        />
      )}
      <ModalErrorAlert message={errorMessage} />
    </InModal>
  );
}

export default IncidentTypesFormDialog;
